import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';
import { ref, get } from 'firebase/database';
import firebaseConfig from '../../config';

const { databaseId } = firebaseConfig;

const calculateProgressiveBonus = (user, visibleQuestionCount) => {
  const totalEngagementActions = user.comments + user.replies + user.visits;
  const questionsSquared = visibleQuestionCount * visibleQuestionCount;
  const progressiveFactor = Math.min(
    1,
    totalEngagementActions / Math.max(1, questionsSquared)
  );
  return progressiveFactor * 5; // Max 5 bonus points
};

// Helper function to convert HTML to plain text
const htmlToText = (html) => {
  if (!html) return '';
  // Remove HTML tags and decode entities
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"');
  return text.trim();
};

// Helper function to calculate content quality based on comment/reply length
const calculateContentQuality = (comments, replies) => {
  let totalContentLength = 0;
  let qualityScore = 0;
  let commentCount = 0;

  // Analyze comment lengths
  comments.forEach((comment) => {
    const textContent = htmlToText(
      comment.html || comment.message || comment.text || ''
    );
    const { length } = textContent;
    totalContentLength += length;
    commentCount += 1;

    // Quality scoring based on length thresholds
    if (length >= 500) {
      qualityScore += 3; // Thoughtful comment (500+ chars)
    } else if (length >= 150) {
      qualityScore += 2; // Decent comment (150-499 chars)
    } else if (length >= 10) {
      qualityScore += 1; // Minimal comment (10-149 chars)
    }
    // Comments under 10 chars get 0 points
  });

  // Analyze reply lengths (bonus for community engagement)
  replies.forEach((reply) => {
    const textContent = htmlToText(
      reply.html || reply.message || reply.text || ''
    );
    const { length } = textContent;
    totalContentLength += length;

    // Replies get slightly higher scoring as they show community engagement
    if (length >= 150) {
      qualityScore += 2.5; // Thoughtful reply (150+ chars)
    } else if (length >= 30) {
      qualityScore += 1.5; // Decent reply (30-149 chars)
    } else if (length >= 5) {
      qualityScore += 0.5; // Minimal reply (5-29 chars)
    }
  });

  const averageLength =
    commentCount > 0 ? totalContentLength / commentCount : 0;

  return {
    totalContentLength,
    qualityScore,
    averageLength,
    commentCount: commentCount + replies.length
  };
};

// Helper function to generate date range array
const generateDateRange = (startDate, endDate) => {
  const dates = [];
  const currentDate = new Date(startDate);
  const end = new Date(endDate);
  const today = new Date();

  // Cap the end date at today to avoid showing future dates
  const actualEndDate = end < today ? end : today;

  while (currentDate <= actualEndDate) {
    dates.push(new Date(currentDate).toISOString().split('T')[0]); // YYYY-MM-DD format
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

// Helper function to safely extract JavaScript Date from Firebase timestamps
const getDateFromFirebaseTimestamp = (timestamp) => {
  if (!timestamp) return null;

  // If it's already a JavaScript Date
  if (timestamp instanceof Date) {
    return timestamp;
  }

  // If it has a toDate() method (Firebase Timestamp object)
  if (typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }

  // If it has seconds property (Firestore timestamp format)
  if (timestamp.seconds !== undefined) {
    return new Date(timestamp.seconds * 1000);
  }

  // If it's a number (Unix timestamp in milliseconds)
  if (typeof timestamp === 'number') {
    return new Date(timestamp);
  }

  console.warn('Unknown timestamp format:', timestamp);
  return null;
};

// Helper function to build daily question availability map
const buildDailyQuestionAvailability = (
  visibleQuestions,
  startDate,
  endDate
) => {
  const dateRange = generateDateRange(startDate, endDate);
  const dailyQuestionCounts = {};

  // Initialize all dates with 0 questions
  dateRange.forEach((date) => {
    dailyQuestionCounts[date] = 0;
  });

  // Process each question to determine when it became visible
  visibleQuestions.forEach((question) => {
    if (question.questionBecameVisibleAt) {
      const visibleDate = getDateFromFirebaseTimestamp(
        question.questionBecameVisibleAt
      );

      if (visibleDate) {
        const questionVisibleDateString = visibleDate
          .toISOString()
          .split('T')[0];
        console.log(
          `Question ${question.id} became visible on: ${questionVisibleDateString}`
        );

        // Add this question to all dates from when it became visible onwards
        dateRange.forEach((date) => {
          if (date >= questionVisibleDateString) {
            dailyQuestionCounts[date] += 1;
          }
        });
      } else {
        console.warn(
          `Failed to parse questionBecameVisibleAt for question ${question.id}:`,
          question.questionBecameVisibleAt
        );
      }
    } else {
      console.warn(
        `Question ${question.id} missing questionBecameVisibleAt timestamp, assuming visible from start`
      );
      // If no timestamp, assume question was visible from the beginning
      dateRange.forEach((date) => {
        dailyQuestionCounts[date] += 1;
      });
    }
  });

  return dailyQuestionCounts;
};

// Helper function to calculate sentiment metrics from comments with sentiment data
const calculateSentimentMetrics = (
  allComments,
  allReplies,
  users,
  visibleQuestions
) => {
  const allItems = [...allComments, ...allReplies];

  // Filter items that have sentiment data
  const itemsWithSentiment = allItems.filter(
    (item) => item.sentiment && item.sentiment.score !== undefined
  );

  if (itemsWithSentiment.length === 0) {
    return null; // No sentiment data available
  }

  // Overall sentiment breakdown
  const sentimentCounts = { positive: 0, neutral: 0, negative: 0 };
  let totalScore = 0;
  const emotions = {};

  itemsWithSentiment.forEach((item) => {
    sentimentCounts[item.sentiment.label]++;
    totalScore += item.sentiment.score;

    // Count emotions
    if (item.sentiment.emotions && Array.isArray(item.sentiment.emotions)) {
      item.sentiment.emotions.forEach((emotion) => {
        emotions[emotion] = (emotions[emotion] || 0) + 1;
      });
    }
  });

  const averageScore = totalScore / itemsWithSentiment.length;

  // Per-user sentiment
  const userSentiment = users
    .map((user) => {
      const userItems = itemsWithSentiment.filter(
        (item) => item.uid === user.uid
      );

      if (userItems.length === 0) {
        return {
          userId: user.uid,
          userName: user.name || 'Unknown User',
          averageScore: null,
          label: 'no-data',
          commentCount: 0
        };
      }

      const userTotalScore = userItems.reduce(
        (sum, item) => sum + item.sentiment.score,
        0
      );
      const userAvgScore = userTotalScore / userItems.length;

      // Determine label from average score
      let label = 'neutral';
      if (userAvgScore > 0.3) label = 'positive';
      else if (userAvgScore < -0.3) label = 'negative';

      return {
        userId: user.uid,
        userName: user.name || 'Unknown User',
        averageScore: Math.round(userAvgScore * 100) / 100,
        label,
        commentCount: userItems.length
      };
    })
    .sort((a, b) => (b.averageScore || -2) - (a.averageScore || -2));

  // Per-question sentiment
  const questionSentiment = visibleQuestions
    .map((question) => {
      const questionItems = itemsWithSentiment.filter(
        (item) => item.questionId === question.id
      );

      if (questionItems.length === 0) {
        return {
          questionId: question.id,
          questionTitle: question.order
            ? `Question ${question.order}`
            : question.title || `Question ${question.id}`,
          questionOrder: question.order || 999,
          averageScore: null,
          label: 'no-data',
          commentCount: 0
        };
      }

      const questionTotalScore = questionItems.reduce(
        (sum, item) => sum + item.sentiment.score,
        0
      );
      const questionAvgScore = questionTotalScore / questionItems.length;

      let label = 'neutral';
      if (questionAvgScore > 0.3) label = 'positive';
      else if (questionAvgScore < -0.3) label = 'negative';

      return {
        questionId: question.id,
        questionTitle: question.order
          ? `Question ${question.order}`
          : question.title || `Question ${question.id}`,
        questionOrder: question.order || 999,
        averageScore: Math.round(questionAvgScore * 100) / 100,
        label,
        commentCount: questionItems.length
      };
    })
    .sort((a, b) => a.questionOrder - b.questionOrder);

  // Top emotions
  const topEmotions = Object.entries(emotions)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([emotion, count]) => ({ emotion, count }));

  return {
    overview: {
      totalAnalysed: itemsWithSentiment.length,
      totalComments: allItems.length,
      averageScore: Math.round(averageScore * 100) / 100,
      breakdown: {
        positive: sentimentCounts.positive,
        neutral: sentimentCounts.neutral,
        negative: sentimentCounts.negative,
        positivePercent: Math.round(
          (sentimentCounts.positive / itemsWithSentiment.length) * 100
        ),
        neutralPercent: Math.round(
          (sentimentCounts.neutral / itemsWithSentiment.length) * 100
        ),
        negativePercent: Math.round(
          (sentimentCounts.negative / itemsWithSentiment.length) * 100
        )
      }
    },
    userSentiment,
    questionSentiment,
    topEmotions
  };
};

// Helper function to calculate response time statistics per question
const calculateQuestionResponseTimes = (visibleQuestions, allComments) => {
  const responseTimeData = [];

  visibleQuestions.forEach((question) => {
    // Get when the question became visible
    const questionVisibleTime = question.questionBecameVisibleAt
      ? getDateFromFirebaseTimestamp(question.questionBecameVisibleAt)
      : null;

    if (!questionVisibleTime) {
      console.warn(
        `Question ${question.id} missing questionBecameVisibleAt, skipping response time calculation`
      );
      return;
    }

    // Find all first comments for this question (one per user)
    const firstCommentsByUser = {};

    allComments.forEach((comment) => {
      if (comment.questionId === question.id && comment.timestamp) {
        const commentTime = getDateFromFirebaseTimestamp(comment.timestamp);
        if (commentTime) {
          // Track first comment per user
          if (
            !firstCommentsByUser[comment.uid] ||
            commentTime < firstCommentsByUser[comment.uid]
          ) {
            firstCommentsByUser[comment.uid] = commentTime;
          }
        }
      }
    });

    // Calculate response times in minutes
    const responseTimes = Object.values(firstCommentsByUser).map(
      (commentTime) => {
        const diffMs = commentTime - questionVisibleTime;
        return Math.max(0, diffMs / (1000 * 60)); // Convert to minutes, ensure non-negative
      }
    );

    // Only include questions that have at least one response
    if (responseTimes.length > 0) {
      // Calculate statistics
      const sorted = [...responseTimes].sort((a, b) => a - b);
      const median =
        sorted.length % 2 === 0
          ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
          : sorted[Math.floor(sorted.length / 2)];

      const q1Index = Math.floor(sorted.length * 0.25);
      const q3Index = Math.floor(sorted.length * 0.75);

      // Generate question title
      let questionTitle;
      if (question.order) {
        questionTitle = `Question ${question.order}`;
      } else {
        questionTitle =
          question.title || question.name || `Question ${question.id}`;
      }

      responseTimeData.push({
        questionId: question.id,
        questionTitle,
        questionOrder: question.order || 999,
        responseTimes,
        count: responseTimes.length,
        median: Math.round(median * 10) / 10,
        q1: Math.round(sorted[q1Index] * 10) / 10,
        q3: Math.round(sorted[q3Index] * 10) / 10,
        min: Math.round(sorted[0] * 10) / 10,
        max: Math.round(sorted[sorted.length - 1] * 10) / 10,
        mean:
          Math.round(
            (responseTimes.reduce((sum, t) => sum + t, 0) /
              responseTimes.length) *
              10
          ) / 10
      });
    }
  });

  // Sort by question order
  return responseTimeData.sort((a, b) => a.questionOrder - b.questionOrder);
};

// Helper function to calculate user response times for parallel coordinates chart
const calculateUserResponseTimes = (users, visibleQuestions, allComments) => {
  const userResponseData = [];

  users.forEach((user) => {
    const responses = [];
    const responseTimes = [];

    visibleQuestions.forEach((question) => {
      // Get when the question became visible
      const questionVisibleTime = question.questionBecameVisibleAt
        ? getDateFromFirebaseTimestamp(question.questionBecameVisibleAt)
        : null;

      if (!questionVisibleTime) {
        // Skip this question if no visible time
        responses.push({
          questionId: question.id,
          questionTitle: question.order
            ? `Question ${question.order}`
            : question.title || `Question ${question.id}`,
          questionOrder: question.order || 999,
          responseTime: null // Missing data
        });
        return;
      }

      // Find user's first comment for this question (only top-level comments, not replies)
      let firstCommentTime = null;

      allComments.forEach((comment) => {
        if (
          comment.questionId === question.id &&
          comment.uid === user.uid &&
          !comment.parentCommentId &&
          comment.timestamp
        ) {
          const commentTime = getDateFromFirebaseTimestamp(comment.timestamp);
          if (commentTime) {
            if (!firstCommentTime || commentTime < firstCommentTime) {
              firstCommentTime = commentTime;
            }
          }
        }
      });

      // Calculate response time if user commented
      let responseTime = null;
      if (firstCommentTime) {
        const diffMs = firstCommentTime - questionVisibleTime;
        responseTime = Math.max(0, diffMs / (1000 * 60)); // Convert to minutes
        responseTimes.push(responseTime);
      }

      // Generate question title
      let questionTitle;
      if (question.order) {
        questionTitle = `Question ${question.order}`;
      } else {
        questionTitle =
          question.title || question.name || `Question ${question.id}`;
      }

      responses.push({
        questionId: question.id,
        questionTitle,
        questionOrder: question.order || 999,
        responseTime:
          responseTime !== null ? Math.round(responseTime * 10) / 10 : null
      });
    });

    // Only include users who responded to at least one question
    if (responseTimes.length > 0) {
      // Calculate median response time for color coding
      const sortedTimes = [...responseTimes].sort((a, b) => a - b);
      const median =
        sortedTimes.length % 2 === 0
          ? (sortedTimes[sortedTimes.length / 2 - 1] +
              sortedTimes[sortedTimes.length / 2]) /
            2
          : sortedTimes[Math.floor(sortedTimes.length / 2)];

      // Sort responses by question order
      responses.sort((a, b) => a.questionOrder - b.questionOrder);

      userResponseData.push({
        userId: user.uid,
        userName: user.name || 'Unknown User',
        responses,
        medianResponseTime: Math.round(median * 10) / 10,
        completeness: responseTimes.length, // How many questions answered
        totalQuestions: visibleQuestions.length
      });
    }
  });

  // Sort users by median response time (fastest first)
  return userResponseData.sort(
    (a, b) => a.medianResponseTime - b.medianResponseTime
  );
};

// Helper function to calculate question interaction heatmap (user × question matrix)
const calculateQuestionInteractionMatrix = (
  users,
  visibleQuestions,
  allComments,
  allReplies
) => {
  const matrix = [];

  // Create a map to count interactions per user per question
  const interactionCounts = {};

  // Initialize counts for all users and questions
  users.forEach((user) => {
    interactionCounts[user.uid] = {};
    visibleQuestions.forEach((question) => {
      interactionCounts[user.uid][question.id] = 0;
    });
  });

  // Count comments per user per question
  allComments.forEach((comment) => {
    if (comment.uid && comment.questionId && interactionCounts[comment.uid]) {
      if (interactionCounts[comment.uid][comment.questionId] !== undefined) {
        interactionCounts[comment.uid][comment.questionId]++;
      }
    }
  });

  // Count replies per user per question
  allReplies.forEach((reply) => {
    if (reply.uid && reply.questionId && interactionCounts[reply.uid]) {
      if (interactionCounts[reply.uid][reply.questionId] !== undefined) {
        interactionCounts[reply.uid][reply.questionId]++;
      }
    }
  });

  // Build matrix array for heatmap visualization
  users.forEach((user) => {
    visibleQuestions.forEach((question) => {
      // Generate question title using same logic as question popularity
      let questionTitle;
      if (question.order) {
        questionTitle = `Question ${question.order}`;
      } else {
        questionTitle =
          question.title || question.name || `Question ${question.id}`;
      }

      matrix.push({
        userName: user.name,
        userId: user.uid,
        questionId: question.id,
        questionTitle,
        interactionCount: interactionCounts[user.uid][question.id] || 0
      });
    });
  });

  return matrix;
};


// Helper function to calculate question popularity metrics
const calculateQuestionPopularity = (
  allComments,
  allReplies,
  visibleQuestions
) => {
  // Create a map to track engagement per question
  const questionEngagementMap = {};

  // Initialize with all visible questions
  visibleQuestions.forEach((question) => {
    // Generate question title using order field for question numbers
    let questionTitle;
    if (question.order) {
      questionTitle = `Question ${question.order}`;
    } else {
      questionTitle =
        question.title || question.name || `Question ${question.id}`;
    }

    questionEngagementMap[question.id] = {
      id: question.id,
      title: questionTitle,
      order: question.order || null,
      directComments: 0,
      associatedReplies: 0,
      uniqueUsers: new Set(),
      totalEngagement: 0
    };
  });

  // Process direct comments
  allComments.forEach((comment) => {
    const { questionId } = comment;
    if (questionEngagementMap[questionId]) {
      questionEngagementMap[questionId].directComments += 1;
      questionEngagementMap[questionId].uniqueUsers.add(comment.uid);
      questionEngagementMap[questionId].totalEngagement += 1;
    }
  });

   // Process replies directly (they have questionId too)
   console.log(`Processing ${allReplies.length} replies...`);

  allReplies.forEach((reply, index) => {
    console.log(`Processing reply ${index}:`, {
      uid: reply.uid,
      questionId: reply.questionId,
      parentCommentId: reply.parentCommentId,
      availableFields: Object.keys(reply)
    });

    const { questionId } = reply;

    if (questionId && questionEngagementMap[questionId]) {
      questionEngagementMap[questionId].associatedReplies += 1;
      questionEngagementMap[questionId].uniqueUsers.add(reply.uid);
      questionEngagementMap[questionId].totalEngagement += 1;
      console.log(
        `✅ Reply ${index} successfully linked to question ${questionId}`
      );
    } else {
      console.log(
        `❌ Reply ${index} - Question ID ${questionId} not found in engagement map or missing`
      );
      console.log(
        'Available question IDs:',
        Object.keys(questionEngagementMap)
      );
    }
  });

  // Convert to array and finalize metrics
  const questionPopularityArray = Object.values(questionEngagementMap).map(
    (question) => ({
      id: question.id,
      title: question.title,
      order: question.order,
      directComments: question.directComments,
      associatedReplies: question.associatedReplies,
      uniqueUsers: question.uniqueUsers.size, // Convert Set to count
      totalEngagement: question.totalEngagement,
      name: question.title // For chart compatibility
    })
  );

  // Sort by question order (consecutive numbering)
  questionPopularityArray.sort((a, b) => (a.order || 0) - (b.order || 0));

  return questionPopularityArray;
};

// Function to calculate engagement score for a user (0-100) - Content Quality Focus
function calculateEngagementScore(
  user,
  maxContentQuality,
  visibleQuestionCount,
  userComments = [],
  userReplies = [],
  returnBreakdown = false
) {
  // Minimum engagement thresholds - dynamic based on visible questions
  const MIN_TIME_THRESHOLD = 30 * Math.max(1, visibleQuestionCount); // 30 seconds per question minimum
  const hasMeaningfulTime = user.sessionTime >= MIN_TIME_THRESHOLD;
  const hasMeaningfulInteraction = user.comments > 0;

  // Early exit for minimal engagement - max 5% score
  if (!hasMeaningfulTime && !hasMeaningfulInteraction) {
    const minimalScore = Math.round(Math.min(5, user.visits * 1.5));
    if (returnBreakdown) {
      return {
        score: minimalScore,
        breakdown: {
          baseContentScore: 0,
          avgLengthBonus: 0,
          coverageScore: 0,
          communityBonus: 0,
          timeScore: 0,
          visitScore: minimalScore,
          contentAdjustment: 0,
          interactionAdjustment: 0,
          finalScore: minimalScore,
          contentQualityMetrics: {
            totalContentLength: 0,
            averageLength: 0,
            commentCount: 0
          }
        }
      };
    }
    return minimalScore;
  }

  // Content Quality Component (50% weight) - PRIMARY FOCUS
  let contentScore = 0;
  let baseContentScore = 0;
  let avgLengthBonus = 0;
  let contentQuality = {
    qualityScore: 0,
    averageLength: 0,
    totalContentLength: 0,
    commentCount: 0
  };

  if (hasMeaningfulInteraction) {
    contentQuality = calculateContentQuality(userComments, userReplies);

    // Base content scoring with quality emphasis
    const qualityRatio =
      maxContentQuality > 0
        ? Math.min(1, contentQuality.qualityScore / maxContentQuality)
        : 0;
    baseContentScore = qualityRatio * 40;

    // Length bonus for substantial content (up to 10 additional points)
    avgLengthBonus = Math.min(10, contentQuality.averageLength / 50); // 1 point per 50 chars average, max 10

    contentScore = Math.min(50, baseContentScore + avgLengthBonus);
  }

  // Interaction Breadth Component (25% weight) - Question coverage + community engagement
  let interactionScore = 0;
  let coverageScore = 0;
  let communityBonus = 0;

  if (visibleQuestionCount > 0 && hasMeaningfulInteraction) {
    // Question coverage scoring
    const questionCoverage = user.questionEngagement
      ? Object.keys(user.questionEngagement).length / visibleQuestionCount
      : Math.min(1, user.comments / visibleQuestionCount);

    coverageScore = questionCoverage * 15; // 15 points for full coverage

    // Community engagement bonus
    const replyRatio =
      user.comments > 0 ? Math.min(1, user.replies / user.comments) : 0;
    communityBonus = replyRatio * 10; // 10 points for active community participation

    interactionScore = Math.min(25, coverageScore + communityBonus);
  }

  // Basic Time Component (15% weight) - Quality gate only
  let timeScore = 0;
  const fullTimeThreshold = 60 * Math.max(1, visibleQuestionCount);
  const partialTimeThreshold = 30 * Math.max(1, visibleQuestionCount);

  if (user.sessionTime >= fullTimeThreshold) {
    timeScore = 15; // Full points for meaningful time per question
  } else if (user.sessionTime >= partialTimeThreshold) {
    timeScore = 10; // Partial points for minimal time per question
  } else if (user.sessionTime > 0) {
    timeScore = 5; // Very low points for any time
  }

  // Visit Frequency Component (10% weight) - Return engagement
  const maxExpectedVisits = Math.max(1, visibleQuestionCount); // Dynamic based on visible questions
  const visitScore =
    (Math.min(user.visits, maxExpectedVisits) / maxExpectedVisits) * 10;

  // Calculate final score without adjustments
  // Note: No availability multiplier needed since all users are notified of new questions
  // and have equal opportunity to engage with all visible questions
  const finalScore = contentScore + interactionScore + timeScore + visitScore;

  // Remove harsh caps - let content quality drive high scores
  const roundedScore = Math.round(Math.min(100, finalScore));

  if (returnBreakdown) {
    return {
      score: roundedScore,
      breakdown: {
        baseContentScore: Math.round(baseContentScore * 100) / 100,
        avgLengthBonus: Math.round(avgLengthBonus * 100) / 100,
        coverageScore: Math.round(coverageScore * 100) / 100,
        communityBonus: Math.round(communityBonus * 100) / 100,
        timeScore: Math.round(timeScore * 100) / 100,
        visitScore: Math.round(visitScore * 100) / 100,
        contentAdjustment: 0, // No adjustments - all users have equal opportunity via email notifications
        interactionAdjustment: 0,
        finalScore: roundedScore,
        // Additional metadata for tooltips
        contentQualityMetrics: {
          totalContentLength: contentQuality.totalContentLength,
          averageLength: Math.round(contentQuality.averageLength),
          commentCount: contentQuality.commentCount
        }
      }
    };
  }

  return roundedScore;
}

// Helper function to calculate daily engagement scores for each user
const calculateDailyEngagementScores = (
  allComments,
  allReplies,
  users,
  userCommentsMap,
  userRepliesMap,
  startDate,
  endDate,
  dailyQuestionCounts
) => {
  if (!dailyQuestionCounts) {
    console.error('No daily question counts provided - this should not happen');
    return [];
  }

  // Calculate real max content quality for proper normalization (same as main analytics)
  const allContentQualities = [];
  users.forEach((user) => {
    const userComments = userCommentsMap[user.uid] || [];
    const userReplies = userRepliesMap[user.uid] || [];
    const contentQuality = calculateContentQuality(userComments, userReplies);
    allContentQualities.push(contentQuality.qualityScore);
  });
  const maxContentQuality = Math.max(...allContentQualities, 1); // Ensure minimum of 1

  const dateRange = generateDateRange(startDate, endDate);
  const dailyData = {};

  // Initialize all dates with zero engagement for all users
  dateRange.forEach((date) => {
    dailyData[date] = {
      date
    };
    // Initialize each user's engagement to 0 for this date (will be updated with validatedUsers later)
    users.forEach((user) => {
      if (user.name) {
        dailyData[date][user.name] = 0;
      }
    });
  });

  // Validate and normalize user data structure
  const validatedUsers = users
    .map((user) => {
      if (!user.uid) {
        console.error('User missing uid:', user);
        return null;
      }
      if (!user.name) {
        console.warn(`User ${user.uid} missing name, using UID as fallback`);
      }

      const validatedUser = {
        uid: user.uid,
        name: user.name || `User-${user.uid.substring(0, 8)}`, // Fallback name
        sessionTime: user.sessionTime || 0,
        visits: user.visits || 1,
        questionEngagement: user.questionEngagement || {}
      };

      return validatedUser;
    })
    .filter((user) => user !== null);

  // Group comments and replies by user and date
  const userActivityByDate = {};
  validatedUsers.forEach((user) => {
    userActivityByDate[user.uid] = {};
    dateRange.forEach((date) => {
      userActivityByDate[user.uid][date] = {
        comments: [],
        replies: []
      };
    });
  });

  [...allComments, ...allReplies].forEach((item) => {
    const parsedDate = getDateFromFirebaseTimestamp(item.timestamp);

    if (parsedDate) {
      const itemDate = parsedDate.toISOString().split('T')[0];
      const { uid } = item;

      if (userActivityByDate[uid] && userActivityByDate[uid][itemDate]) {
        if (item.parentCommentId) {
          userActivityByDate[uid][itemDate].replies.push(item);
        } else {
          userActivityByDate[uid][itemDate].comments.push(item);
        }
      }
    }
  });

  // Calculate cumulative engagement scores for each user by date using the SAME algorithm
  validatedUsers.forEach((user) => {
    let cumulativeComments = [];
    let cumulativeReplies = [];

    dateRange.forEach((date) => {
      // Add today's comments/replies to cumulative totals
      if (userActivityByDate[user.uid] && userActivityByDate[user.uid][date]) {
        const todaysComments = userActivityByDate[user.uid][date].comments;
        const todaysReplies = userActivityByDate[user.uid][date].replies;

        cumulativeComments = [...cumulativeComments, ...todaysComments];
        cumulativeReplies = [...cumulativeReplies, ...todaysReplies];
      }

      // Calculate engagement score based on cumulative data up to this date
      if (cumulativeComments.length > 0 || cumulativeReplies.length > 0) {
        // Get the number of questions visible on this specific date
        const questionsVisibleOnDate = dailyQuestionCounts[date] || 0;

        // Create user stats object with cumulative data (same structure as main analytics)
        const userStats = {
          comments: cumulativeComments.length,
          replies: cumulativeReplies.length,
          sessionTime: user.sessionTime || 0, // Use total session time
          visits: user.visits || 1,
          questionEngagement: user.questionEngagement || {} // Use existing question engagement data
        };

        // Use progressive question count instead of static total
        const engagementScore = calculateEngagementScore(
          userStats,
          maxContentQuality, // Use real calculated max content quality
          questionsVisibleOnDate, // Use date-specific question count
          cumulativeComments,
          cumulativeReplies
        );

        dailyData[date][user.name] = engagementScore;
      }
    });
  });

  const result = dateRange.map((date) => {
    const formattedDate = new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });

    const dayData = {
      date: formattedDate
    };

    // Add each user's engagement score for this day
    validatedUsers.forEach((user) => {
      const score = dailyData[date][user.name];
      dayData[user.name] = score;
    });

    return dayData;
  });

  return result;
};

// Helper function to calculate daily activity data (activity length in seconds)
const calculateDailyActivity = (presenceData, users, startDate, endDate) => {
  const dateRange = generateDateRange(startDate, endDate);
  const dailyData = {};

  // Initialize all dates with zero activity for all users
  dateRange.forEach((date) => {
    dailyData[date] = {
      date
    };
    // Initialize each user's activity to 0 for this date
    users.forEach((user) => {
      dailyData[date][user.name] = 0;
    });
  });

  // Process each user's presence data
  users.forEach((user) => {
    const userPresence = presenceData[user.uid];
    if (!userPresence || !userPresence.sessionHistory) return;

    // Use daily activity data from sessionHistory
    dateRange.forEach((dateString) => {
      const dayHistory = userPresence.sessionHistory[dateString];
      if (dayHistory && dayHistory.totalSeconds) {
        // Keep in seconds for accuracy (no rounding errors)
        dailyData[dateString][user.name] = dayHistory.totalSeconds;
      }
      // else: stays at 0 (initialized above)
    });
  });

  // Format final data
  return dateRange.map((date) => {
    const dayData = {
      date: new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })
    };

    // Add each user's activity for this day
    users.forEach((user) => {
      dayData[user.name] = dailyData[date][user.name];
    });

    return dayData;
  });
};

function AnalyticsFactory(firebase) {
  const getSessionAnalytics = async (
    sessionId,
    startDate = null,
    endDate = null
  ) => {
    try {
      // 1. Get session info
      const sessionDoc = await getDoc(
        doc(firebase.fsdb, 'sessions', sessionId)
      );
      if (!sessionDoc.exists()) {
        throw new Error('Session not found');
      }
      const sessionData = sessionDoc.data();

      // 2. Get presence data from Realtime Database
      const presenceRef = ref(firebase.rtdb, `userPresence/${sessionId}`);
      const presenceSnapshot = await get(presenceRef);
      const presenceData = presenceSnapshot.exists()
        ? presenceSnapshot.val()
        : {};

      // 3. Get ALL users who have access to this session
      const allUsersRef = collection(firebase.fsdb, 'users');
      const allUsersSnapshot = await getDocs(allUsersRef);

      const users = {};
      const nonModeratorUsers = [];
      const sessionAccessUsers = new Set(); // Track users with session access

      // First, collect all users and identify those with session access
      allUsersSnapshot.docs.forEach((userDoc) => {
        const userData = userDoc.data();
        const uid = userDoc.id;
        users[uid] = userData;

        // Check if user has access to this session (not a moderator)
        const isModerator =
          userData.isModerator &&
          Array.isArray(userData.isModerator) &&
          userData.isModerator.includes(sessionId);

        // Check if user has session access (multiple possible field names)
        const hasSessionAccess =
        (userData.sessionsUserHasAccessTo &&
          Array.isArray(userData.sessionsUserHasAccessTo) &&
          userData.sessionsUserHasAccessTo.includes(sessionId)) ||
        (userData.sessionAccess &&
          Array.isArray(userData.sessionAccess) &&
          userData.sessionAccess.includes(sessionId)) ||
        (userData.sessions &&
          Array.isArray(userData.sessions) &&
          userData.sessions.includes(sessionId));

        if (hasSessionAccess && !isModerator) {
          sessionAccessUsers.add(uid);
          nonModeratorUsers.push({
            uid,
            name: userData.name || 'Unknown User',
            sessionTime: presenceData[uid]?.totalSessionTime || 0,
            visits: presenceData[uid]?.totalVisits || 0,
            comments: 0 // Will be populated below
          });
        }
      });

      // Also include users from presence data who might not have been caught above
      // (in case there's a different access mechanism we haven't identified)
      const presenceUserIds = Object.keys(presenceData);
      presenceUserIds.forEach((uid) => {
        if (!sessionAccessUsers.has(uid)) {
          const userData = users[uid];
          if (userData) {
            // Filter out moderators
            const isModerator =
              userData.isModerator &&
              Array.isArray(userData.isModerator) &&
              userData.isModerator.includes(sessionId);

            if (!isModerator) {
              nonModeratorUsers.push({
                uid,
                name: userData.name || 'Unknown User',
                sessionTime: presenceData[uid]?.totalSessionTime || 0,
                visits: presenceData[uid]?.totalVisits || 0,
                comments: 0 // Will be populated below
              });
            }
          }
        }
      });

      // 4. Get comment counts
      const questionsRef = collection(
        firebase.fsdb,
        'sessions',
        sessionId,
        'questions'
      );
      const questionsSnapshot = await getDocs(questionsRef);

      // Count visible questions for engagement score calculation
      const visibleQuestions = questionsSnapshot.docs
        .filter((_doc) => {
          const questionData = _doc.data();
          return questionData.isVisible === true;
        })
        .map((_doc) => ({
          id: _doc.id,
          ..._doc.data()
        }));

      const visibleQuestionCount = visibleQuestions.length;

      // Build daily question availability map for timeline calculations
      let dailyQuestionCounts = null;
      if (startDate && endDate) {
        dailyQuestionCounts = buildDailyQuestionAvailability(
          visibleQuestions,
          startDate,
          endDate
        );
      }

      const commentCounts = {};
      const questionEngagementByUser = {}; // Track which questions each user engaged with
      let totalComments = 0;

      await Promise.all(
        questionsSnapshot.docs.map(async (questionDoc) => {
          const commentsRef = collection(
            firebase.fsdb,
            'sessions',
            sessionId,
            'questions',
            questionDoc.id,
            'comments'
          );
          const commentsSnapshot = await getDocs(commentsRef);

          commentsSnapshot.docs.forEach((commentDoc) => {
            const { uid } = commentDoc.data();

            // Only count comments from non-moderators
            const isModerator =
              users[uid]?.isModerator &&
              Array.isArray(users[uid].isModerator) &&
              users[uid].isModerator.includes(sessionId);

            if (!isModerator) {
              commentCounts[uid] = (commentCounts[uid] || 0) + 1;
              totalComments += 1;

              // Track question engagement for this user
              if (!questionEngagementByUser[uid]) {
                questionEngagementByUser[uid] = {};
              }
              questionEngagementByUser[uid][questionDoc.id] = true;
            }
          });
        })
      );

      // Update user comment counts and calculate replies
      await Promise.all(
        nonModeratorUsers.map(async (user) => {
          user.comments = commentCounts[user.uid] || 0;
          user.questionEngagement = questionEngagementByUser[user.uid] || {};

          // Count replies for this user
          let replyCount = 0;
          await Promise.all(
            questionsSnapshot.docs.map(async (questionDoc) => {
              const commentsRef = collection(
                firebase.fsdb,
                'sessions',
                sessionId,
                'questions',
                questionDoc.id,
                'comments'
              );
              const commentsSnapshot = await getDocs(commentsRef);

              commentsSnapshot.docs.forEach((commentDoc) => {
                const comment = commentDoc.data();
                if (comment.uid === user.uid && comment.parentCommentId) {
                  replyCount += 1;
                }
              });
            })
          );
          user.replies = replyCount;
        })
      );

      // 5. Get device usage data
      const deviceSessionsQuery = query(
        collection(firebase.fsdb, 'deviceSessions'),
        where('sessionId', '==', sessionId)
      );
      const deviceSessionsSnapshot = await getDocs(deviceSessionsQuery);

      const deviceCounts = { mobile: 0, tablet: 0, desktop: 0 };

      // DIAGNOSTIC: Track device sessions per user
      const deviceSessionsByUser = {};
      const deviceSessionDetails = [];

      // First pass: collect device sessions by user (for diagnostics and proportions)
      deviceSessionsSnapshot.docs.forEach((_doc) => {
        const device = _doc.data();
        const { uid, deviceType, timestamp } = device;

        // Only count visits from non-moderators
        const isModerator =
          users[uid]?.isModerator &&
          Array.isArray(users[uid].isModerator) &&
          users[uid].isModerator.includes(sessionId);

        if (!isModerator) {
          // DIAGNOSTIC: Track sessions per user
          if (!deviceSessionsByUser[uid]) {
            deviceSessionsByUser[uid] = [];
          }
          deviceSessionsByUser[uid].push({
            deviceType,
            timestamp
          });

          deviceSessionDetails.push({
            uid,
            userName: users[uid]?.name || 'Unknown',
            deviceType,
            timestamp
          });
        }
      });

      // NEW: Proportional allocation based on user visit totals
      // For each user, allocate their total visits proportionally across their device types
      nonModeratorUsers.forEach((user) => {
        const { uid, visits } = user;
        const userVisits = visits || 0;
        const userDeviceSessions = deviceSessionsByUser[uid] || [];

        if (userVisits > 0 && userDeviceSessions.length > 0) {
          // Count device type usage for this user
          const userDeviceCounts = { mobile: 0, tablet: 0, desktop: 0 };
          userDeviceSessions.forEach((session) => {
            userDeviceCounts[session.deviceType] =
              (userDeviceCounts[session.deviceType] || 0) + 1;
          });

          const totalUserSessions = userDeviceSessions.length;

          // Allocate user's total visits proportionally to device types
          Object.entries(userDeviceCounts).forEach(
            ([deviceType, sessionCount]) => {
              if (sessionCount > 0) {
                const proportion = sessionCount / totalUserSessions;
                const allocatedVisits = userVisits * proportion;
                deviceCounts[deviceType] =
                  (deviceCounts[deviceType] || 0) + allocatedVisits;
              }
            }
          );
        }
      });

      // Calculate total device visits (should now match User Breakdown total)
      const totalDeviceVisits = Object.values(deviceCounts).reduce(
        (sum, count) => sum + count,
        0
      );

      const deviceUsage = Object.entries(deviceCounts)
        .filter(([_, count]) => count > 0)
        .map(([type, count]) => ({
          name: type.charAt(0).toUpperCase() + type.slice(1),
          value:
            totalDeviceVisits > 0
              ? Math.round((count / totalDeviceVisits) * 100)
              : 0,
          count: Math.round(count) // Round to integer for display
        }));

      // 6. Collect comment content for quality analysis and daily engagement
      const userCommentsMap = {};
      const userRepliesMap = {};
      const allContentQualities = [];
      const allComments = []; // For daily engagement calculation
      const allReplies = []; // For daily engagement calculation

      // Process comments to collect content for each user
      await Promise.all(
        questionsSnapshot.docs.map(async (questionDoc) => {
          const commentsRef = collection(
            firebase.fsdb,
            'sessions',
            sessionId,
            'questions',
            questionDoc.id,
            'comments'
          );
          const commentsSnapshot = await getDocs(commentsRef);

          commentsSnapshot.docs.forEach((commentDoc) => {
            const comment = {
              id: commentDoc.id,
              questionId: questionDoc.id,
              ...commentDoc.data()
            };
            const { uid } = comment;

            // Check if moderator
            const isModerator =
              users[uid]?.isModerator &&
              Array.isArray(users[uid].isModerator) &&
              users[uid].isModerator.includes(sessionId);

            // Always add to allComments/allReplies (includes moderators for Excel export)
            if (comment.parentCommentId) {
              allReplies.push(comment);
            } else {
              allComments.push(comment);
            }

            // Only add to user-specific maps for non-moderators (for analytics)
            if (!isModerator) {
              // Initialize user arrays if not exists
              if (!userCommentsMap[uid]) {
                userCommentsMap[uid] = [];
                userRepliesMap[uid] = [];
              }

              // Add to user-specific arrays
              if (comment.parentCommentId) {
                userRepliesMap[uid].push(comment);
              } else {
                userCommentsMap[uid].push(comment);
              }
            }
          });
        })
      );

      // Calculate question popularity metrics
      const questionPopularity = calculateQuestionPopularity(
        allComments,
        allReplies,
        visibleQuestions
      );
      console.log('Question popularity data:', questionPopularity);

      // Calculate question interaction heatmap matrix
      console.log('Calculating question interaction matrix...');
      const questionInteractionMatrix = calculateQuestionInteractionMatrix(
        nonModeratorUsers,
        visibleQuestions,
        allComments,
        allReplies
      );
      console.log(
        'Question interaction matrix data:',
        questionInteractionMatrix.length,
        'entries'
      );

      // Calculate question response time statistics
      console.log('Calculating question response times...');
      const questionResponseTimes = calculateQuestionResponseTimes(
        visibleQuestions,
        allComments
      );
      console.log('Question response times data:', questionResponseTimes);

      // Calculate user response times for parallel coordinates chart
      console.log('Calculating user response times...');
      const userResponseTimes = calculateUserResponseTimes(
        nonModeratorUsers,
        visibleQuestions,
        allComments
      );
      console.log('User response times data:', userResponseTimes);

      // Calculate sentiment metrics if sentiment data exists
      console.log('Calculating sentiment metrics...');

      // Filter out moderator comments for sentiment analysis
      const nonModeratorComments = allComments.filter((comment) => {
        const isModerator =
          users[comment.uid]?.isModerator &&
          Array.isArray(users[comment.uid].isModerator) &&
          users[comment.uid].isModerator.includes(sessionId);
        return !isModerator;
      });

      const nonModeratorReplies = allReplies.filter((reply) => {
        const isModerator =
          users[reply.uid]?.isModerator &&
          Array.isArray(users[reply.uid].isModerator) &&
          users[reply.uid].isModerator.includes(sessionId);
        return !isModerator;
      });

      const sentimentData = calculateSentimentMetrics(
        nonModeratorComments,
        nonModeratorReplies,
        nonModeratorUsers,
        visibleQuestions
      );
      console.log(
        'Sentiment data:',
        sentimentData ? 'Available' : 'No sentiment data found'
      );

      // Calculate content quality for each user and find maximum
      nonModeratorUsers.forEach((user) => {
        const userComments = userCommentsMap[user.uid] || [];
        const userReplies = userRepliesMap[user.uid] || [];
        const contentQuality = calculateContentQuality(
          userComments,
          userReplies
        );

        user.contentQuality = contentQuality;
        allContentQualities.push(contentQuality.qualityScore);
      });

      // Find maximum content quality for normalization
      const maxContentQuality = Math.max(...allContentQualities, 1); // Ensure minimum of 1

      // Calculate engagement score for each user with content quality focus
      nonModeratorUsers.forEach((user) => {
        const userComments = userCommentsMap[user.uid] || [];
        const userReplies = userRepliesMap[user.uid] || [];
        const result = calculateEngagementScore(
          user,
          maxContentQuality,
          visibleQuestionCount,
          userComments,
          userReplies,
          true // Request breakdown
        );
        user.engagementScore = result.score;
        user.qualityBreakdown = result.breakdown;
      });

      // 7. Calculate totals
      const totalSessionTime = nonModeratorUsers.reduce(
        (sum, user) => sum + user.sessionTime,
        0
      );
      const totalVisits = nonModeratorUsers.reduce(
        (sum, user) => sum + user.visits,
        0
      );

      const averageEngagementScore =
        nonModeratorUsers.length > 0
          ? Math.round(
              nonModeratorUsers.reduce(
                (sum, user) => sum + user.engagementScore,
                0
              ) / nonModeratorUsers.length
            )
          : 0;

      // 8. Calculate daily activity timeline if dates are provided
      let activityTimeline = [];
      if (startDate && endDate) {
        activityTimeline = calculateDailyActivity(
          presenceData,
          nonModeratorUsers,
          startDate,
          endDate
        );
      }

      // 9. Calculate daily engagement timeline if dates are provided
      let engagementTimeline = [];
      if (startDate && endDate) {
        engagementTimeline = calculateDailyEngagementScores(
          allComments,
          allReplies,
          nonModeratorUsers,
          userCommentsMap,
          userRepliesMap,
          startDate,
          endDate,
          dailyQuestionCounts
        );
      }

      return {
        sessionId,
        sessionName: sessionData.name || 'Unnamed Session',
        totals: {
          users: nonModeratorUsers.length,
          sessionTime: totalSessionTime,
          comments: totalComments,
          visits: totalVisits,
          engagementScore: averageEngagementScore
        },
        users: nonModeratorUsers.sort((a, b) => b.sessionTime - a.sessionTime), // Sort by session time desc
        deviceUsage,
        activityTimeline,
        engagementTimeline,
        questionPopularity,
        questionInteractionMatrix,
        questionResponseTimes,
        userResponseTimes,
        sentimentData, // Can be null if no sentiment analysis has been performed
        allComments, // All top-level comments with IDs
        allReplies // All replies with IDs
      };
    } catch (error) {
      console.error('Error fetching session analytics:', error);
      throw error;
    }
  };

  return {
    getSessionAnalytics
  };
}

export default AnalyticsFactory;
